import { CurrencyPipe, DecimalPipe, TitleCasePipe } from '@angular/common';

export type Currency =
  | 'AUD'
  | 'BRL'
  | 'CHF'
  | 'CNY'
  | 'CAD'
  | 'CZK'
  | 'EUR'
  | 'GBP'
  | 'HKD'
  | 'INR'
  | 'JPY'
  | 'KHR'
  | 'KRW'
  | 'MXN'
  | 'NOK'
  | 'NZD'
  | 'RUB'
  | 'SEK'
  | 'SGD'
  | 'TRY'
  | 'USD'
  | 'ZAR';

export type BillingFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'once' | 'other';

export enum MarketplaceBillingFrequency {
  OneTime = 'One Time',
  Yearly = 'Yearly',
  Monthly = 'Monthly',
}

export function formatDisplayPrice(
  value: any,
  currency: Currency | string,
  billingFrequency?: BillingFrequency | MarketplaceBillingFrequency,
  isCents = true,
  translateZero = true,
  displaySymbol: 'code' | 'symbol' | 'symbol-narrow' | string | boolean = true,
  isStartingPrice = false,
): string {
  let price = 'Contact Sales';
  let display: string;
  let priceFormatFunc = defaultFormatFunc;

  if (typeof displaySymbol === 'string') {
    display = displaySymbol;
  } else {
    display = displaySymbol === true ? 'symbol' : 'code';
  }

  switch (currency) {
    case 'CZK':
      display = displaySymbol ? 'symbol-narrow' : 'code';
      priceFormatFunc = CZKFormatFunc;
      break;
  }

  if (value) {
    if (isCents) {
      value = convertCentsToDollars(Number(value));
    }
    price = priceFormatFunc(value, currency, display);
    if (billingFrequency) {
      price = `${price} ${formatBillingFrequency(billingFrequency)}`;
    }
    if (isStartingPrice) {
      price = `Starting at ${price}`;
    }
  }

  if (translateZero) {
    if (value < 0) {
      return 'Contact Sales';
    }
    if (value === 0) {
      return 'Free';
    }
  }
  if (value === 0) {
    return priceFormatFunc(value, currency, display);
  }

  return price;
}

export function convertCentsToDollars(centValue: number): number {
  if (!centValue) {
    return centValue;
  }

  if (!Number.isInteger(centValue)) {
    centValue = Number(centValue);
    centValue = Math.round(centValue);
  }
  return centValue / 100;
}

export function convertDollarsToCents(dollarValue: number): string {
  return ((dollarValue || 0) / 100).toFixed(2);
}

export function formatBillingFrequency(value?: string): string {
  let returnValue = '';
  if (value) {
    switch (value.toLowerCase()) {
      case 'once':
      case 'one time':
        returnValue = 'One Time';
        break;
      default:
        returnValue = new TitleCasePipe().transform(value);
    }
  }
  return returnValue;
}

function defaultFormatFunc(value: string, currency: string, display: string): string {
  if (!currency) {
    return new DecimalPipe('en-US').transform(value, '1.2-2') ?? '';
  } else {
    return new CurrencyPipe('en-US').transform(value, currency, display, '1.2-2') ?? '';
  }
}

function CZKFormatFunc(value: string, currency: string, display: string): string {
  const price = (new DecimalPipe('en-US').transform(value, '1.0-2') ?? '').replace('.', ',');
  const currencySymbol = (new CurrencyPipe('en-US').transform(1, currency, display, '0.0') ?? '').split('1')[0];
  return `${price} ${currencySymbol} bez DPH`;
}
